// TrajeRentalSystemView.js - Versión Dorado Profesional
import React from 'react';
import { 
  ShoppingBag, Check, Clock, Users, MessageCircle, RefreshCw, Wifi, WifiOff 
} from 'lucide-react';

// Componentes auxiliares extraídos
const LoadingSpinner = ({ message, submessage }) => (
  <div className="min-h-screen bg-gradient-to-br from-amber-50 to-yellow-50 flex items-center justify-center p-4">
    <div className="text-center">
      <RefreshCw className="h-8 w-8 text-amber-600 animate-spin mx-auto mb-4" />
      <p className="text-gray-800 text-lg font-medium">{message}</p>
      {submessage && <p className="text-gray-600 text-sm mt-1">{submessage}</p>}
    </div>
  </div>
);

const EmptyState = ({ filtroCategoria, onShowAll }) => (
  <div className="flex flex-col items-center justify-center py-12">
    <div className="bg-gradient-to-br from-amber-100 to-yellow-100 p-6 rounded-full mb-4">
      <ShoppingBag className="h-16 w-16 text-amber-600" />
    </div>
    <p className="text-gray-800 text-lg font-medium">No hay trajes disponibles</p>
    <p className="text-gray-600 text-sm mt-1">
      {filtroCategoria === 'Todos' 
        ? 'No se encontraron trajes en el sistema' 
        : `No hay trajes en la categoría: ${filtroCategoria}`}
    </p>
    {filtroCategoria !== 'Todos' && (
      <button 
        onClick={onShowAll} 
        className="mt-4 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white px-6 py-2 rounded-lg shadow-md transition-all duration-300 transform hover:scale-105"
      >
        Ver todos
      </button>
    )}
  </div>
);

const TallaButton = ({ talla, info, onClick, variant = 'small' }) => {
  const isSmall = variant === 'small';
  const baseClasses = `${isSmall ? 'px-2 py-1' : 'px-2 py-1'} rounded-full text-xs font-medium flex items-center space-x-1 transition-all duration-200`;
  const statusClasses = info.disponible
    ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200 hover:from-green-200 hover:to-emerald-200 cursor-pointer shadow-sm hover:shadow-md'
    : 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border border-red-200 cursor-not-allowed opacity-70';

  return (
    <button
      onClick={() => info.disponible && onClick()}
      disabled={!info.disponible}
      className={`${baseClasses} ${statusClasses}`}
    >
      <span>{talla}</span>
      <span>({info.cantidad || 0})</span>
      {info.disponible && <Check className="h-2 w-2 sm:h-3 sm:w-3" />}
    </button>
  );
};

const TrajeCard = ({ traje, onShowTallas, onReserve, getImageUrl, handleImageError, handleImageLoad }) => {
  const tallasEntries = Object.entries(traje.tallas);
  const visibleTallas = tallasEntries.slice(0, 4);
  const hasMoreTallas = tallasEntries.length > 4;
  const hasAvailableTallas = Object.values(traje.tallas).some(info => info.disponible);

  return (
    <div className="bg-white rounded-xl shadow-lg border border-amber-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden h-full flex flex-col">
      {/* Imagen */}
      <div className="relative flex-shrink-0">
        <img
          src={getImageUrl(traje)}
          alt={traje.nombre}
          className="w-full h-48 sm:h-64 object-cover"
          onError={(e) => handleImageError(e, traje)}
          onLoad={() => handleImageLoad(traje)}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
        <div className="absolute top-2 right-2 sm:top-3 sm:right-3">
          <span className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-3 py-1 rounded-full text-xs font-medium shadow-md">
            {traje.categoria || 'Sin categoría'}
          </span>
        </div>
      </div>

      {/* Contenido */}
      <div className="p-4 sm:p-6 flex-1 flex flex-col">
        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{traje.nombre}</h3>
        <div className="h-10 mb-3">
          <p className="text-gray-600 text-sm line-clamp-2 leading-5">{traje.descripcion || 'Sin descripción'}</p>
        </div>
        
        <div className="flex items-center justify-between mb-4">
          <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-amber-600 to-yellow-600 bg-clip-text text-transparent">
            S/ {traje.precio}
          </span>
          <span className="text-xs sm:text-sm text-gray-500 bg-gray-50 px-2 py-1 rounded-full">por día</span>
        </div>

        {/* Tallas disponibles */}
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700 mb-2 flex items-center">
            <span className="w-2 h-2 bg-gradient-to-r from-amber-400 to-yellow-400 rounded-full mr-2"></span>
            Tallas:
          </p>
          <div className="flex flex-wrap gap-1 sm:gap-2">
            {visibleTallas.map(([talla, info]) => (
              <TallaButton
                key={talla}
                talla={talla}
                info={info}
                onClick={() => onReserve(traje, talla)}
              />
            ))}
            {hasMoreTallas && (
              <span className="text-xs text-gray-500 px-2 py-1 bg-gray-50 rounded-full">+{tallasEntries.length - 4}</span>
            )}
          </div>
          {hasAvailableTallas && (
            <p className="text-xs text-gray-500 mt-2 flex items-center">
              <span className="mr-1">✨</span>
              Toca una talla para reservar
            </p>
          )}
        </div>

        {/* Botón ver tallas */}
        <button
          onClick={() => onShowTallas(traje)}
          className="w-full bg-white border-2 border-yellow-500 hover:border-yellow-400 text-black font-medium py-2.5 sm:py-3 px-4 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 text-sm sm:text-base shadow-md hover:shadow-lg transform hover:scale-105 mt-auto"
        >
          <Users className="h-4 w-4 text-yellow-600" />
          <span>Ver Todas las Tallas</span>
        </button>

      </div>
    </div>
  );
};

const TallaModal = ({ traje, onClose, onReserve, getImageUrl, handleImageError, handleImageLoad }) => {
  if (!traje) return null;

  const tallasEntries = Object.entries(traje.tallas);
  const availableCount = tallasEntries.filter(([, info]) => info.disponible).length;
  const totalUnits = Object.values(traje.tallas).reduce((total, info) => total + (info.cantidad || 0), 0);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50">
      <div className="bg-white rounded-t-xl sm:rounded-xl w-full sm:max-w-lg sm:max-h-[90vh] max-h-[85vh] overflow-y-auto shadow-2xl border border-amber-100">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-amber-100 sticky top-0 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">Tallas Disponibles</h3>
              <p className="text-gray-600 mt-1 text-sm">{traje.nombre}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-amber-600 text-2xl font-bold w-8 h-8 flex items-center justify-center transition-colors rounded-full hover:bg-amber-100"
            >
              ×
            </button>
          </div>
        </div>

        {/* Imagen */}
        <div className="px-4 sm:px-6 pt-4">
          <div className="relative">
            <img
              src={getImageUrl(traje)}
              alt={traje.nombre}
              className="w-full h-40 sm:h-48 object-contain rounded-lg bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-100"
              onError={(e) => handleImageError(e, traje)}
              onLoad={() => handleImageLoad(traje)}
            />
          </div>
        </div>

        {/* Contenido */}
        <div className="p-4 sm:p-6">
          {/* Info del traje */}
          <div className="mb-4 sm:mb-6">
            <p className="text-gray-600 mb-3 text-sm">{traje.descripcion || 'Sin descripción'}</p>
            <div className="flex items-center justify-between mb-4">
              <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-amber-600 to-yellow-600 bg-clip-text text-transparent">
                S/ {traje.precio}
              </span>
              <span className="bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 px-3 py-1 rounded-full text-xs font-medium border border-amber-200">
                {traje.categoria || 'Sin categoría'}
              </span>
            </div>
          </div>

          {/* Tallas */}
          <div>
            <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
              <span className="w-3 h-3 bg-gradient-to-r from-amber-400 to-yellow-400 rounded-full mr-2"></span>
              Estado de Tallas
            </h4>
            <div className="space-y-2 sm:space-y-3">
              {tallasEntries.map(([talla, info]) => (
                <div
                  key={talla}
                  className={`p-3 sm:p-4 rounded-lg border-2 transition-all duration-200 ${
                    info.disponible 
                      ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 shadow-sm' 
                      : 'bg-gradient-to-r from-red-50 to-rose-50 border-red-200 opacity-75'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2 sm:mb-3">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <span className="text-xl sm:text-2xl font-bold text-gray-800">{talla}</span>
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        {info.disponible ? (
                          <>
                            <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                            <span className="text-green-700 font-medium text-sm">Disponible</span>
                          </>
                        ) : (
                          <>
                            <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />
                            <span className="text-red-700 font-medium text-sm">Agotado</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      info.disponible 
                        ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200' 
                        : 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border border-red-200'
                    }`}>
                      {info.cantidad || 0} unidades
                    </div>
                  </div>
                  
                  {info.disponible && (
                    <button
                      onClick={() => onReserve(traje, talla)}
                      className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 text-sm shadow-md hover:shadow-lg transform hover:scale-105"
                    >
                      <MessageCircle className="h-4 w-4" />
                      <span>Reservar Talla {talla}</span>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Resumen */}
          <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-200">
            <h5 className="font-medium text-gray-900 mb-2 text-sm flex items-center">
              <span className="w-2 h-2 bg-gradient-to-r from-amber-400 to-yellow-400 rounded-full mr-2"></span>
              Resumen
            </h5>
            <div className="grid grid-cols-2 gap-4 text-xs sm:text-sm">
              <div>
                <span className="text-gray-600">Disponibles:</span>
                <div className="font-medium text-green-600">{availableCount} de {tallasEntries.length}</div>
              </div>
              <div>
                <span className="text-gray-600">Total:</span>
                <div className="font-medium bg-gradient-to-r from-amber-600 to-yellow-600 bg-clip-text text-transparent">
                  {totalUnits} unidades
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente principal simplificado
const TrajeRentalSystemView = (props) => {
  const {
    trajes, categorias, filtroCategoria, modalTallas, loading, error, isOnline, cachedImages,
    setFiltroCategoria, setModalTallas, cargarTrajes, mostrarTallas, enviarWhatsApp,
    formatLastUpdate, getImageUrl, handleImageError, handleImageLoad
  } = props;

  // Estados de carga y error
  if (loading && trajes.length === 0) {
    return <LoadingSpinner message="Cargando trajes..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-yellow-50 flex items-center justify-center p-4">
        <div className="text-center max-w-sm bg-white p-6 rounded-xl shadow-lg border border-amber-200">
          <p className="text-red-600 mb-4 text-sm">{error}</p>
          <button 
            onClick={cargarTrajes} 
            className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white px-4 py-2 rounded-lg w-full transition-all duration-300 transform hover:scale-105 shadow-md"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-yellow-50 to-white">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-md shadow-lg border-b border-amber-100 sticky top-0 z-40">
        <div className="px-3 py-4 sm:px-4 sm:py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              
              <div>
                <h1 className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-amber-600 to-yellow-600 bg-clip-text text-transparent font-serif">
                  BELLAS BOUTIQUE
                </h1>
                <p className="text-xs text-gray-600 font-medium hidden sm:block">VESTIDOS, TERNOS Y ACCESORIOS</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {/* Indicador de conexión */}
              <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs transition-colors ${
                isOnline 
                  ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 border border-green-200' 
                  : 'bg-gradient-to-r from-red-100 to-rose-100 text-red-700 border border-red-200'
              }`}>
                {isOnline ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                <span className="hidden sm:inline">{isOnline ? 'Online' : 'Offline'}</span>
              </div>

              {/* Botón de recarga */}
              <button
                onClick={cargarTrajes}
                disabled={loading}
                className="flex items-center space-x-1 bg-gradient-to-r from-amber-100 to-yellow-100 hover:from-amber-200 hover:to-yellow-200 disabled:opacity-50 px-2 py-1 sm:px-3 sm:py-2 rounded-lg transition-all duration-300 border border-amber-200 shadow-sm hover:shadow-md"
              >
                <RefreshCw className={`h-3 w-3 sm:h-4 sm:w-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="text-xs sm:text-sm text-amber-800">Actualizar</span>
              </button>

              <div className="bg-gradient-to-r from-amber-100 to-yellow-100 px-2 py-1 sm:px-4 sm:py-2 rounded-lg border border-amber-200 shadow-sm">
                <span className="bg-gradient-to-r from-amber-600 to-yellow-600 bg-clip-text text-transparent font-medium text-xs sm:text-sm">
                  {trajes.length} <span className="hidden sm:inline">Trajes</span>
                </span>
              </div>
            </div>
          </div>
          {/* <div className="mt-2 text-xs text-gray-500">
            <span>Última actualización: {formatLastUpdate()}</span>
          </div> */}
        </div>
      </header>

      <div className="px-3 py-4 sm:px-4 sm:py-8 max-w-7xl mx-auto">
        {/* Filtros */}
        <div className="mb-8">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {categorias.map(categoria => (
              <button
                key={categoria}
                onClick={() => setFiltroCategoria(categoria)}
                className={`px-4 py-2 rounded-full font-medium transition-all duration-300 whitespace-nowrap text-sm shadow-md hover:shadow-lg transform hover:scale-105 ${
                  filtroCategoria === categoria
                    ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white border border-amber-300'
                    : 'bg-white text-gray-700 hover:bg-gradient-to-r hover:from-amber-100 hover:to-yellow-100 border border-amber-200 hover:border-amber-300'
                }`}
              >
                {categoria}
              </button>
            ))}
          </div>
        </div>

        {/* Contenido principal */}
        {loading ? (
          <LoadingSpinner 
            message="Buscando trajes..." 
            submessage={filtroCategoria === 'Todos' ? 'Cargando todos los trajes' : `Buscando en categoría: ${filtroCategoria}`}
          />
        ) : trajes.length === 0 ? (
          <EmptyState 
            filtroCategoria={filtroCategoria} 
            onShowAll={() => setFiltroCategoria('Todos')} 
          />
        ) : (
          <>
            {/* Grid de trajes */}
            {/* Grid de trajes */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 auto-rows-fr">
              {trajes.map(traje => (
                <div
                  key={traje.id}
                  className="bg-gradient-to-r from-yellow-400 to-amber-500 p-[1px] rounded-xl flex"
                >
                  <div className="bg-white rounded-xl flex-1 flex flex-col">
                    <TrajeCard
                      traje={traje}
                      onShowTallas={mostrarTallas}
                      onReserve={enviarWhatsApp}
                      getImageUrl={getImageUrl}
                      handleImageError={handleImageError}
                      handleImageLoad={handleImageLoad}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Info de estado */}
            <div className="mt-8 sm:mt-12 text-center">
              <div className="inline-flex items-center space-x-2 px-4 py-3 rounded-full text-xs sm:text-sm font-medium bg-white/80 backdrop-blur-sm text-gray-700 border border-amber-200 shadow-md">
                <RefreshCw className="h-3 w-3 text-amber-600" />
                <span>Actualización manual • Última vez: {formatLastUpdate()}</span>
              </div>
              {cachedImages.size > 0 && (
                <div className="mt-3 text-xs text-gray-500 bg-white/60 backdrop-blur-sm px-3 py-1 rounded-full inline-block border border-amber-100">
                  📷 {cachedImages.size} imágenes en cache
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      <TallaModal
        traje={modalTallas}
        onClose={() => setModalTallas(null)}
        onReserve={enviarWhatsApp}
        getImageUrl={getImageUrl}
        handleImageError={handleImageError}
        handleImageLoad={handleImageLoad}
      />
    </div>
  );
};

export default TrajeRentalSystemView;